import { useState, useEffect } from 'react';
import { CollectibleObject } from '../types';
import { supabase } from '../lib/supabaseClient';
import { getPublicStorageUrl } from '../utils/storage';
import { toCamelCase } from '../utils/caseConversion';

export const useObjects = () => {
  const [objects, setObjects] = useState<CollectibleObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchObjects = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔄 Chargement des objets depuis Supabase...');

        // Charger les objets depuis Supabase (le client Supabase gère son propre timeout)
        const { data: objectsData, error: objectsError } = await supabase
          .from('elements')
          .select('*')
          .eq('type', 'object')
          .order('created_at', { ascending: false });

        if (objectsError) {
          throw new Error(`Erreur Supabase: ${objectsError.message}`);
        }

        // Charger tous les effets en une seule requête
        const { data: allEffects } = await supabase
          .from('special_item_effects')
          .select('item_id, effect_id');

        // Mapper les effets par item_id
        const effectsMap = new Map<number, string[]>();
        (allEffects || []).forEach((effect: any) => {
          if (!effectsMap.has(effect.item_id)) {
            effectsMap.set(effect.item_id, []);
          }
          effectsMap.get(effect.item_id)!.push(effect.effect_id);
        });

        // Ajouter les effets à chaque élément
        const objectsWithEffects = (objectsData || []).map(element => ({
          ...element,
          grantedEffects: effectsMap.get(element.id) || []
        }));

        // Convert snake_case from database to camelCase for JavaScript
        const objectsDataCamelCase = toCamelCase(objectsWithEffects);

        // Transform Supabase format to CollectibleObject interface
        const transformedObjects: CollectibleObject[] = (objectsDataCamelCase || []).map((obj: any) => {
          // Parse baseEffects if it's a string
          const baseEffects = typeof obj.baseEffects === 'string'
            ? JSON.parse(obj.baseEffects || '[]')
            : obj.baseEffects || [];

          return {
            id: obj.id,
            name: obj.translations.fr.name, // Default to French
            emoji: getPublicStorageUrl(obj.assetUrl), // Now in camelCase
            bookId: obj.rewardFrom?.bookId || 1, // Now in camelCase
            bookTitle: obj.rewardFrom?.bookTitle || 'LilHero', // Now in camelCase
            isSpecial: obj.isSpecial || false, // Now in camelCase
            isShiny: obj.isShiny || baseEffects.includes('shiny'), // Now in camelCase
            isBurning: baseEffects.includes('burning'),
            isFrozen: baseEffects.includes('frozen'),
            grantedEffects: obj.grantedEffects || [], // Effets accordés par cet objet spécial
            hasBeenPublished: obj.hasBeenPublished || false // A été publié dans une histoire visible
          };
        });

        console.log(`✅ ${transformedObjects.length} objets chargés depuis Supabase`);
        setObjects(transformedObjects);

      } catch (err) {
        console.error('❌ Erreur lors du chargement des objets:', err);
        setError(err instanceof Error ? err.message : 'Erreur inconnue');

        // Fallback to empty array
        setObjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchObjects();
  }, []);

  return {
    objects,
    loading,
    error,
    refetch: () => {
      setLoading(true);
      setError(null);
      // Re-run the effect
      window.location.reload();
    }
  };
};

export default useObjects;
